import React, { useState, useEffect } from 'react';
import { X, Plus, ArrowRight, Code } from 'lucide-react';

interface SoarFieldMappingFormProps {
  initialMapping?: Record<string, string>;
  onChange: (mapping: Record<string, string>) => void;
}

const SoarFieldMappingForm: React.FC<SoarFieldMappingFormProps> = ({
  initialMapping = {},
  onChange
}) => {
  // Create a deep copy of the initialMapping object for the initial state
  const [mapping, setMapping] = useState<Record<string, string>>(() => {
    return initialMapping ? JSON.parse(JSON.stringify(initialMapping)) : {};
  });
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [showJsonPreview, setShowJsonPreview] = useState(false);
  const [jsonInput, setJsonInput] = useState('');
  const [jsonError, setJsonError] = useState('');
  const [errors, setErrors] = useState({ key: '', value: '' });

  // Update parent component when mapping changes
  useEffect(() => {
    // Only call onChange when this component initially mounts or when initialMapping changes
    // This prevents calling onChange on every render which causes an infinite loop
    if (JSON.stringify(mapping) !== JSON.stringify(initialMapping)) {
      onChange(mapping);
    }
  }, [mapping, onChange, initialMapping]);

  // Add a new key-value pair
  const addMapping = () => {
    // Validate
    const newErrors = { key: '', value: '' };
    
    if (!newKey.trim()) {
      newErrors.key = 'Field key is required';
    }
    
    if (!newValue.trim()) {
      newErrors.value = 'Field value is required';
    }
    
    if (newErrors.key || newErrors.value) {
      setErrors(newErrors);
      return;
    }
    
    // Add the new mapping
    setMapping(prevMapping => ({
      ...prevMapping,
      [newKey]: newValue
    }));
    
    // Reset form
    setNewKey('');
    setNewValue('');
    setErrors({ key: '', value: '' });
  };

  // Remove a key-value pair
  const removeMapping = (key: string) => {
    setMapping(prevMapping => {
      const newMapping = { ...prevMapping };
      delete newMapping[key];
      return newMapping;
    });
  };

  // Handle Enter key in the input fields
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addMapping();
    }
  };

  // Parse JSON input and populate the mapping
  const handleJsonApply = () => {
    try {
      // Clean up JSON string - handle trailing commas which are invalid in standard JSON
      const cleanedJson = jsonInput
        .replace(/,(\s*[}\]])/g, '$1') // Remove trailing commas
        .trim();
      
      const parsedJson = JSON.parse(cleanedJson);
      
      // Validate that the JSON is a simple key-value object
      if (typeof parsedJson !== 'object' || Array.isArray(parsedJson) || parsedJson === null) {
        setJsonError('JSON must be a simple key-value object');
        return;
      }
      
      // Convert all values to strings
      const stringifiedMapping: Record<string, string> = {};
      Object.entries(parsedJson).forEach(([key, value]) => {
        stringifiedMapping[key] = String(value);
      });
      
      // Update the mapping
      setMapping(stringifiedMapping);
      setJsonError('');
      setShowJsonPreview(false);
    } catch (error) {
      setJsonError('Invalid JSON format. Please check for syntax errors.');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-medium text-gray-300">Soar Field Mapping</h3>
        <button
          type="button"
          className="btn-add text-xs px-2 py-1 flex items-center gap-1"
          onClick={() => setShowJsonPreview(!showJsonPreview)}
        >
          <Code size={14} />
          {showJsonPreview ? 'Hide JSON' : 'Show JSON'}
        </button>
      </div>
      
      {/* JSON Preview and Input */}
      {showJsonPreview && (
        <div className="p-3 bg-background-hover rounded-md border border-gray-700/40">
          <textarea
            className="w-full bg-background-hover text-gray-300 border border-gray-700/50 rounded-md p-2 text-xs font-mono min-h-[100px]"
            placeholder={'Paste your JSON here... {"soarField1": "logField1", "soarField2": "logField2"}'}
            value={jsonInput}
            onChange={(e) => setJsonInput(e.target.value)}
          />
          {jsonError && <p className="text-red-500 text-xs mt-1">{jsonError}</p>}
          <div className="flex justify-end mt-2">
            <button
              type="button"
              className="btn-primary text-xs px-3 py-1"
              onClick={handleJsonApply}
            >
              Apply JSON
            </button>
          </div>
          {Object.keys(mapping).length > 0 && (
            <div className="mt-3 pt-3 border-t border-gray-700/40">
              <p className="text-xs text-gray-400 mb-1">Current Mapping:</p>
              <pre className="text-xs text-gray-300">
                {JSON.stringify(mapping, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
      
      {/* Mapping entries */}
      {Object.keys(mapping).length > 0 ? (
        <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
          {Object.entries(mapping).map(([key, value]) => (
            <div 
              key={key}
              className="flex items-center bg-background-hover rounded-md p-2 border border-gray-700/40"
            >
              <div className="flex-1 flex items-center gap-2 text-sm">
                <span className="px-2 py-1 text-blue-400 rounded text-xs">
                  {key}
                </span>
                <ArrowRight size={14} className="text-gray-500" />
                <span className="px-2 py-1 text-green-400 rounded text-xs">
                  {value}
                </span>
              </div>
              <button 
                type="button"
                className="p-1 text-gray-500 hover:text-gray-300 transition-colors"
                onClick={() => removeMapping(key)}
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-gray-500 text-sm text-center py-2 border border-dashed border-gray-700 rounded-md">
          No field mappings added yet
        </div>
      )}
      
      {/* Add new mapping form */}
      <div className="grid grid-cols-5 gap-2">
        <div className="col-span-2">
          <input
            type="text"
            className={`w-full bg-background-hover border ${errors.key ? 'border-red-500/50' : 'border-gray-700/50'} text-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary/40 focus:outline-none transition-all`}
            placeholder="Soar Field"
            value={newKey}
            onChange={(e) => {
              setNewKey(e.target.value);
              if (errors.key) setErrors({ ...errors, key: '' });
            }}
            onKeyDown={handleKeyDown}
          />
          {errors.key && <p className="text-red-500 text-xs mt-1">{errors.key}</p>}
        </div>
        
        <div className="col-span-2">
          <input
            type="text"
            className={`w-full bg-background-hover border ${errors.value ? 'border-red-500/50' : 'border-gray-700/50'} text-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary/30 focus:border-primary/40 focus:outline-none transition-all`}
            placeholder="Log Field"
            value={newValue}
            onChange={(e) => {
              setNewValue(e.target.value);
              if (errors.value) setErrors({ ...errors, value: '' });
            }}
            onKeyDown={handleKeyDown}
          />
          {errors.value && <p className="text-red-500 text-xs mt-1">{errors.value}</p>}
        </div>
        
        <div className="col-span-1">
          <button
            type="button"
            className="w-full h-full btn-add flex items-center justify-center"
            onClick={addMapping}
          >
            <Plus size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default SoarFieldMappingForm; 